import { useCallback, useMemo, useRef, useState } from 'react';

import type { QuestionnaireRequirements } from 'clients/Communication/Questionnaire.dto';
import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { getSchedulerType } from '@/features/nylas-calendar-manage-connection-modal';
import { getNylasInterviewType } from '@/features/nylas-calendar-manage-connection-modal/lib';
import { InterviewType } from '@/features/nylas-calendar-manage-connection-modal';
import { useContactCandidateMutation } from 'hooks/shared/useContactCandidateMutation/useContactCandidateMutation';
import type { IndeedProject } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedProjectListQuery';
import type { IndeedJobContact } from 'shared/legacy/hooks/useBrowserExtension/indeed/indeed.types';
import { CampaignJobboard } from 'shared/legacy/contexts/SelectedCampaignContext/SelectedCampaignContext.types';
import type { CandidateStatus } from 'services/candidates';

import type { BulkSendSlot, BulkSendProgress } from './use-bulk-send-pipeline';

export type IndeedBulkSendItem = {
  esPersonId: string;
  candidateName: string;
  campaignId: number;
  candidateStatus: CandidateStatus;
  subject: string;
  message: string;
  improvedJobId: number;
  questionnaire?: QuestionnaireRequirements;
  isAutoSendInterviewInviteEnabled?: boolean;
  schedulerType?: InterviewType;
};

export type IndeedBulkSendParams = {
  indeedProject: IndeedProject;
  indeedJob: IndeedJobContact | null;
};

const EMPTY_MAP = new Map<string, BulkSendSlot>();

export function useBulkIndeedSendPipeline(concurrency = 3) {
  const [slots, setSlots] = useState<Map<string, BulkSendSlot>>(EMPTY_MAP);
  const queueRef = useRef<IndeedBulkSendItem[]>([]);
  const itemsMapRef = useRef<Map<string, IndeedBulkSendItem>>(new Map());
  const paramsRef = useRef<IndeedBulkSendParams | null>(null);
  const isRunningRef = useRef(false);
  const initialCountRef = useRef(0);
  const onSuccessRef = useRef<((esPersonId: string) => void) | null>(null);
  const contactCandidateMutation = useContactCandidateMutation();

  const updateSlot = useCallback((esPersonId: string, patch: Partial<BulkSendSlot>) => {
    setSlots((prev) => {
      const next = new Map(prev);
      const current = next.get(esPersonId);
      if (current) {
        next.set(esPersonId, { ...current, ...patch });
      }
      return next;
    });
  }, []);

  const sendOne = useCallback(
    async (item: IndeedBulkSendItem, params: IndeedBulkSendParams) => {
      const { attempt_id } = (
        await campaignsClient.createRjIndeedOutreachAttempt({
          campaign_id: item.campaignId,
          es_person_id: item.esPersonId,
          improved_job_id: item.improvedJobId,
          metadata: {
            indeed_project_key: params.indeedProject.key,
            indeed_project_name: params.indeedProject.name,
          },
        })
      ).data;

      const contactResult = await contactCandidateMutation.mutateAsync({
        sendMethod: 'indeed',
        campaignId: item.campaignId,
        candidateStatus: item.candidateStatus,
        activeJobBoard: CampaignJobboard.Indeed,
        data: {
          esPersonId: item.esPersonId,
          subject: item.subject,
          message: item.message,
          indeedProjectId: params.indeedProject.key,
          indeedJob: params.indeedJob as any,
          improvedJobPreview: undefined,
          job_improved_id: item.improvedJobId,
          isReply: false,
          metadata: null,
          candidateName: item.candidateName,
          isAiLeveraged: true,
        },
      });

      if (!contactResult?.candidate_notification_id) {
        throw new Error('Missing candidate_notification_id from Indeed contact mutation');
      }

      await campaignsClient.confirmRjIndeedOutreachAttempt({
        campaign_id: item.campaignId,
        es_person_id: item.esPersonId,
        attempt_id,
        candidate_notification_id: contactResult.candidate_notification_id,
        questionnaire: item.questionnaire,
        schedule_interview: item.isAutoSendInterviewInviteEnabled,
        scheduler_type: getSchedulerType(getNylasInterviewType()),
      });
    },
    [contactCandidateMutation],
  );

  const processNext = useCallback(async () => {
    const params = paramsRef.current;
    if (!params) return;

    const item = queueRef.current.shift();
    if (!item) return;

    updateSlot(item.esPersonId, { status: 'loading' });

    try {
      await sendOne(item, params);
      updateSlot(item.esPersonId, { status: 'success' });
      onSuccessRef.current?.(item.esPersonId);
    } catch (error) {
      updateSlot(item.esPersonId, {
        status: 'error',
        error: error instanceof Error ? error : new Error(String(error)),
      });
    }

    await processNext();
  }, [updateSlot, sendOne]);

  const send = useCallback(
    (items: IndeedBulkSendItem[], params: IndeedBulkSendParams, onSuccess?: (esPersonId: string) => void) => {
      if (isRunningRef.current) return;
      isRunningRef.current = true;
      initialCountRef.current = items.length;
      paramsRef.current = params;
      onSuccessRef.current = onSuccess ?? null;

      const initial = new Map<string, BulkSendSlot>();
      for (const item of items) {
        initial.set(item.esPersonId, {
          esPersonId: item.esPersonId,
          candidateName: item.candidateName,
          status: 'pending',
          error: null,
          flow: 'indeed',
        });
        itemsMapRef.current.set(item.esPersonId, item);
      }
      setSlots(initial);

      queueRef.current = [...items];
      const workerCount = Math.min(concurrency, items.length);
      for (let i = 0; i < workerCount; i++) {
        processNext();
      }
    },
    [concurrency, processNext],
  );

  const retrySend = useCallback(
    async (esPersonId: string) => {
      const item = itemsMapRef.current.get(esPersonId);
      const params = paramsRef.current;
      if (!item || !params) return;

      updateSlot(esPersonId, { status: 'loading', error: null });

      try {
        await sendOne(item, params);
        updateSlot(esPersonId, { status: 'success' });
        onSuccessRef.current?.(esPersonId);
      } catch (error) {
        updateSlot(esPersonId, {
          status: 'error',
          error: error instanceof Error ? error : new Error(String(error)),
        });
      }
    },
    [updateSlot, sendOne],
  );

  const reset = useCallback(() => {
    queueRef.current = [];
    itemsMapRef.current = new Map();
    paramsRef.current = null;
    isRunningRef.current = false;
    initialCountRef.current = 0;
    onSuccessRef.current = null;
    setSlots(EMPTY_MAP);
  }, []);

  const progress = useMemo<BulkSendProgress>(() => {
    const values = Array.from(slots.values());
    const sent = values.filter((s) => s.status === 'success').length;
    const failed = values.filter((s) => s.status === 'error').length;
    const pending = values.filter((s) => s.status === 'pending' || s.status === 'loading').length;
    return { total: initialCountRef.current, sent, failed, pending };
  }, [slots]);

  const isSending = progress.pending > 0;
  const hasFailed = progress.failed > 0 && !isSending;
  const isComplete = initialCountRef.current > 0 && progress.pending === 0;

  return { slots, progress, isSending, isComplete, hasFailed, send, retrySend, reset };
}
